import { QueryProgramHandle, type DataSourceStoreHandle } from 'fluveria-engine-wasm';
import type { QueryGraphDef } from '../graph/dataGraph.js';
import { WasmProgramCache } from './wasmProgramCache.js';
import type { EventRuntimeManager } from './eventRuntime.js';

export interface QueryEvaluation {
  outputs: Record<string, unknown>;
  dependencies: { sourceFields: Record<string, string[]>; stateKeys: string[]; inputs: string[] };
}

export class QueryRuntimeManager {
  private readonly programs = new WasmProgramCache<QueryProgramHandle>();
  private readonly cached = new Map<string, { signature: string; value: QueryEvaluation }>();
  private readonly lastErrors = new Map<string, string>();
  private readonly unsubscribeState?: () => void;

  constructor(private readonly eventRuntime?: EventRuntimeManager) {
    this.unsubscribeState = eventRuntime?.subscribeChanged(() => this.cached.clear());
  }

  evaluate(graphId: string, graph: QueryGraphDef, store: DataSourceStoreHandle): QueryEvaluation {
    const handle = this.programs.get(graphId, graph, () => new QueryProgramHandle(graph));
    const dependencies = handle.dependencies() as QueryEvaluation['dependencies'];
    const revisionSignature = Object.keys(dependencies.sourceFields).sort().map(sourceId => {
      const status = store.status(sourceId) as { revision?: number; schemaRevision?: number } | undefined;
      return `${sourceId}:${status?.revision ?? -1}:${status?.schemaRevision ?? -1}`;
    }).join('|');
    const signature = `${JSON.stringify(graph)}\u0000${revisionSignature}`;
    const cached = this.cached.get(graphId);
    if (cached?.signature === signature) return cached.value;
    const result = (this.eventRuntime
      ? handle.evaluateWithState(store, this.eventRuntime.handle(), undefined, {})
      : handle.evaluate(store)) as { outputs: Record<string, unknown> };
    const value = { outputs: result.outputs, dependencies };
    this.cached.set(graphId, { signature, value });
    this.lastErrors.delete(graphId);
    return value;
  }

  evaluateLibrary(graphs: Record<string, QueryGraphDef>, store: DataSourceStoreHandle): Record<string, Record<string, unknown>> {
    const result: Record<string, Record<string, unknown>> = {};
    for (const [graphId, graph] of Object.entries(graphs)) {
      try {
        result[graphId] = this.evaluate(graphId, graph, store).outputs;
      } catch (error) {
        this.lastErrors.set(graphId, error instanceof Error ? error.message : String(error));
      }
    }
    return result;
  }

  error(graphId: string): string | undefined { return this.lastErrors.get(graphId); }

  clear(): void {
    this.programs.clear();
    this.cached.clear();
    this.lastErrors.clear();
  }

  dispose(): void { this.unsubscribeState?.(); this.clear(); }
}
